import { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchSupplierNames } from '../services/supplierService';
import AutocompleteInput from '../components/AutocompleteInput';
import './NovaMobile.css';

const API = import.meta.env.VITE_API_URL;
const MAX_BYTES = 30 * 1024 * 1024; // 30 MB
const ALLOWED_EXT = ['pdf', 'xlsx', 'xls', 'doc', 'docx', 'png', 'jpg', 'jpeg'];
const ACCEPT = '.pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg';
const FILE_DESCRIPTIONS = ['New Product List', 'Historical List'];
const DRAFT_KEY = 'nm_draft_v1';

const fmtSize = (b) => (b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.round(b / 1024) + ' KB');
const extOf = (name) => String(name || '').split('.').pop().toLowerCase();

// Which columns to show in the review table — the meaningful product fields, not metadata.
const REVIEW_LABELS = {
  inn: 'INN', molecule_name: 'Molecule', supplier_product_name: 'Product',
  dosage_form: 'Dosage form', strength_value_1: 'Strength', strength_unit_code_1: 'Unit',
  full_strength: 'Full strength', pack_size: 'Pack size', atc_code: 'ATC',
  market_auth_countries: 'MA countries', reference_product_name: 'Reference',
};
const REVIEW_PREFERRED = ['inn', 'molecule_name', 'supplier_product_name', 'dosage_form', 'strength_value_1', 'strength_unit_code_1', 'full_strength', 'pack_size', 'atc_code'];
// Only truly-internal plumbing is hidden; everything else with data is shown so
// we can always see what the extractor actually returned.
const REVIEW_HIDE = new Set(['_type', 'supplier_id', 'supplier_name', 'supplier_headquarter_country', 'source_type', 'created_by', 'created_at', 'updated_at', 'reference_date', 'file_description', 'product_id', 'blob_url', 'archived_at', 'archived_by', 'archive_reason', 'archive_source_file']);
const labelFor = (k) => REVIEW_LABELS[k] || String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
function pickReviewColumns(rows) {
  if (!rows || !rows.length) return [];
  const keys = Object.keys(rows[0]);
  const hasData = (k) => rows.some((r) => r[k] != null && String(r[k]).trim() !== '');
  const chosen = [];
  for (const k of REVIEW_PREFERRED) { if (keys.includes(k) && hasData(k)) chosen.push(k); }
  // then every other non-internal column that actually has data
  for (const k of keys) { if (chosen.length >= 12) break; if (chosen.includes(k) || REVIEW_HIDE.has(k)) continue; if (hasData(k)) chosen.push(k); }
  return chosen.slice(0, 12);
}

/* ---- attachment (paperclip) icon, no emoji ---- */
function PaperclipIcon({ size = 20, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}
function NovaMark({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1L12 2z" fill="#fff" />
    </svg>
  );
}

export default function NovaMobile() {
  const { instance } = useMsal();
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const [step, setStep] = useState('intro'); // intro | form | review | done
  const [messages, setMessages] = useState([
    { from: 'nova', text: 'Hi, I’m Nova. Upload a supplier file (PDF, Excel, Word or image) and I’ll read it, then you review before anything is saved.' },
  ]);
  const [file, setFile] = useState(null);
  const [refDate, setRefDate] = useState('');
  const [fileDesc, setFileDesc] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractRows, setExtractRows] = useState(null);
  const [extractErr, setExtractErr] = useState('');
  const [committing, setCommitting] = useState(false);
  const processIdRef = useRef(null);
  const pollRef = useRef(null);

  const addMsg = useCallback((m) => setMessages((prev) => [...prev, m]), []);

  // supplier names for autocomplete
  useEffect(() => { fetchSupplierNames().then(setSuppliers).catch(() => setSuppliers([])); }, []);

  // auto-scroll chat
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, step, extractRows]);

  // resume previous action
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.step && d.step !== 'done') setResumeDraft(d);
      }
    } catch { /* ignore */ }
  }, []);

  // persist a lightweight draft (file bytes cannot be stored)
  const saveDraft = useCallback((patch) => {
    const d = {
      step, refDate, fileDesc, supplierName,
      fileName: file?.name || null, fileSize: file?.size || null,
      ts: Date.now(), ...patch,
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* ignore */ }
  }, [step, refDate, fileDesc, supplierName, file]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  const resetAll = () => {
    setStep('intro'); setFile(null); setRefDate(''); setFileDesc(''); setSupplierName('');
    setExtractRows(null); setExtractErr(''); setExtracting(false); clearDraft();
  };

  const onPick = () => fileInputRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-pick same file
    if (!f) return;
    const ext = extOf(f.name);
    if (!ALLOWED_EXT.includes(ext)) {
      addMsg({ from: 'warn', text: `“${f.name}” isn’t a supported format. Please upload: PDF, XLSX, XLS, DOC, DOCX, PNG, JPG or JPEG.` });
      return;
    }
    if (f.size > MAX_BYTES) {
      addMsg({ from: 'warn', text: `“${f.name}” is ${fmtSize(f.size)} — over the 30 MB limit. Please upload a smaller file.` });
      return;
    }
    setFile(f);
    addMsg({ from: 'user', text: `Attached: ${f.name} (${fmtSize(f.size)})` });
    addMsg({ from: 'nova', text: 'Great. Add a few details so I file it correctly.' });
    setStep('form');
    saveDraft({ step: 'form', fileName: f.name, fileSize: f.size });
  };

  const formValid = file && refDate && fileDesc && supplierName.trim();

  const acquireToken = useCallback(async () => {
    try {
      const accounts = instance.getAllAccounts();
      if (!accounts.length) return null;
      const resp = await instance.acquireTokenSilent({
        scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`], account: accounts[0],
      });
      return resp.accessToken || resp.idToken;
    } catch { return null; }
  }, [instance]);

  const startReview = async () => {
    const isNew = !suppliers.some((s) => s.toLowerCase() === supplierName.trim().toLowerCase());
    addMsg({ from: 'user', text: `${fileDesc} • ${supplierName}${isNew ? ' (new supplier)' : ''} • ${refDate}` });
    setStep('review'); setExtracting(true); setExtractErr(''); setExtractRows(null);
    saveDraft({ step: 'review' });
    addMsg({ from: 'nova', text: 'Reading your file… I’ll show the rows I found for you to review.' });
    try {
      const token = await acquireToken();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('referenceDate', refDate);
      fd.append('fileDescription', fileDesc);
      fd.append('supplierName', supplierName.trim());
      const r = await fetch(`${API}/api/nova-mobile/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { addMsg({ from: 'warn', text: d.error || `Upload failed (HTTP ${r.status}).` }); setStep('form'); setExtracting(false); return; }
      processIdRef.current = d.process_id;
      pollStatus();
    } catch {
      setExtractErr('pending');
      setExtracting(false);
    }
  };

  const pollStatus = useCallback(async () => {
    const pid = processIdRef.current;
    if (!pid) return;
    try {
      const token = await acquireToken();
      const r = await fetch(`${API}/api/nova-mobile/status/${pid}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const d = await r.json().catch(() => ({}));
      if (d.status === 'review') {
        setExtractRows(Array.isArray(d.rows) ? d.rows : []); setExtracting(false);
        addMsg({ from: 'nova', text: `I found ${Array.isArray(d.rows) ? d.rows.length : 0} rows. Please review, then approve to save.` });
        return;
      }
      if (d.status === 'failed') {
        setExtracting(false); setExtractErr('failed');
        addMsg({ from: 'warn', text: `Couldn’t read the file: ${d.message || 'extraction failed'}.` });
        return;
      }
      pollRef.current = setTimeout(pollStatus, 2500); // queued/extracting/enriching/validating
    } catch {
      pollRef.current = setTimeout(pollStatus, 3000);
    }
  }, [acquireToken, addMsg]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const commit = async () => {
    setCommitting(true);
    try {
      const token = await acquireToken();
      const r = await fetch(`${API}/api/nova-mobile/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ process_id: processIdRef.current, rows: extractRows }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'commit failed');
      const s = d.summary || {};
      const parts = [];
      if (s.inserted != null) parts.push(`${s.inserted} added`);
      if (s.updated) parts.push(`${s.updated} updated`);
      if (s.archived) parts.push(`${s.archived} archived`);
      addMsg({ from: 'nova', text: `Saved to ${s.target === 'history' ? 'the historical table' : 'the supplier product list'}${parts.length ? ` — ${parts.join(', ')}` : ''}. File stored in Azure Blob Storage.` });
      setStep('done'); clearDraft();
    } catch (e) {
      addMsg({ from: 'warn', text: `Couldn’t save: ${e.message || 'please try again'}.` });
    } finally {
      setCommitting(false);
    }
  };

  const doResume = () => {
    const d = resumeDraft;
    setRefDate(d.refDate || ''); setFileDesc(d.fileDesc || ''); setSupplierName(d.supplierName || '');
    setResumeDraft(null);
    setStep('form'); // file bytes can't be restored — ask to re-attach
    addMsg({ from: 'nova', text: `Welcome back. I kept your details${d.supplierName ? ` for ${d.supplierName}` : ''}. Please re-attach “${d.fileName || 'your file'}” to continue.` });
  };
  const discardResume = () => { setResumeDraft(null); clearDraft(); };

  const supplierIsNew = supplierName.trim() &&
    !suppliers.some((s) => s.toLowerCase() === supplierName.trim().toLowerCase());

  return (
    <div className="nm-viewport">
      <div className="nm-phone">
        <div className="nm-head">
          <div className="nm-mark"><NovaMark size={20} /></div>
          <div>
            <div className="nm-title">Nova</div>
            <div className="nm-sub">Supplier file upload</div>
          </div>
        </div>

        <div className="nm-chat">
          {resumeDraft && (
            <div className="nm-resume">
              You have an unfinished upload{resumeDraft.supplierName ? ` for ${resumeDraft.supplierName}` : ''}
              {resumeDraft.fileName ? ` (${resumeDraft.fileName})` : ''}.
              <div className="nm-resume-actions">
                <button className="nm-btn primary" style={{ flex: '0 0 auto', padding: '7px 14px' }} onClick={doResume}>Resume</button>
                <button className="nm-btn ghost" style={{ flex: '0 0 auto', padding: '7px 14px' }} onClick={discardResume}>Discard</button>
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`nm-bubble ${m.from}`}>{m.text}</div>
          ))}

          {/* Metadata form */}
          {step === 'form' && (
            <div className="nm-card">
              <h4>File details</h4>
              {file && (
                <div className="nm-filechip" style={{ marginBottom: 12 }}>
                  <PaperclipIcon size={18} color="#1A8AAF" />
                  <div>
                    <div className="nm-fi">{file.name}</div>
                    <div className="nm-fsz">{fmtSize(file.size)}</div>
                  </div>
                </div>
              )}
              <div className="nm-field">
                <div className="nm-label">Reference date</div>
                <input type="date" className="nm-input" value={refDate}
                  onChange={(e) => { setRefDate(e.target.value); saveDraft({ refDate: e.target.value }); }} />
              </div>
              <div className="nm-field">
                <div className="nm-label">File description</div>
                <select className="nm-select" value={fileDesc}
                  onChange={(e) => { setFileDesc(e.target.value); saveDraft({ fileDesc: e.target.value }); }}>
                  <option value="">Select…</option>
                  {FILE_DESCRIPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                {fileDesc === 'New Product List' && <div className="nm-hint">Added to the supplier product list table.</div>}
                {fileDesc === 'Historical List' && <div className="nm-hint">Added to the supplier’s historical table.</div>}
              </div>
              <div className="nm-field">
                <div className="nm-label">Supplier name</div>
                <AutocompleteInput className="nm-input" value={supplierName}
                  onChange={(v) => { setSupplierName(v); saveDraft({ supplierName: v }); }}
                  suggestions={suppliers} placeholder="Start typing a supplier…" maxItems={8} />
                {supplierIsNew && <span className="nm-new-tag">New supplier — will be created</span>}
              </div>
              <div className="nm-btn-row" style={{ marginTop: 4 }}>
                <button className="nm-btn ghost" onClick={resetAll}>Cancel</button>
                <button className="nm-btn primary" disabled={!formValid} onClick={startReview}>Review</button>
              </div>
            </div>
          )}

          {/* Review step */}
          {step === 'review' && (
            <div className="nm-card">
              <h4>Review before saving</h4>
              <div className="nm-kv" style={{ marginBottom: 10 }}>
                <div><b>File:</b> {file?.name}</div>
                <div><b>Type:</b> {fileDesc}</div>
                <div><b>Supplier:</b> {supplierName}{supplierIsNew ? ' (new)' : ''}</div>
                <div><b>Reference date:</b> {refDate}</div>
              </div>
              {extracting && <div className="nm-spin">Nova is reading your file…</div>}
              {!extracting && extractRows && extractRows.length > 0 && (
                (() => { const cols = pickReviewColumns(extractRows); return cols.length === 0 ? (
                  <div className="nm-bubble warn" style={{ maxWidth: '100%', marginBottom: 0 }}>
                    Nova read {extractRows.length} row{extractRows.length !== 1 ? 's' : ''} but found no field values in them — the file may not contain structured product data the extractor can read. Try a clearer product-list file, or let me know and we&apos;ll check the extraction.
                  </div>
                ) : (
                  <>
                    <div className="nm-hint" style={{ marginBottom: 6 }}>{extractRows.length} rows found (showing first 25).</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="nm-review-table">
                        <thead><tr>{cols.map((k) => <th key={k}>{labelFor(k)}</th>)}</tr></thead>
                        <tbody>
                          {extractRows.slice(0, 25).map((row, i) => (
                            <tr key={i}>{cols.map((k) => <td key={k}>{String(row[k] ?? '')}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ); })()
              )}
              {!extracting && extractRows && extractRows.length === 0 && (
                <div className="nm-bubble warn" style={{ maxWidth: '100%', marginBottom: 0 }}>Nova didn’t find any rows in this file. Check the file and try again.</div>
              )}
              {!extracting && (extractErr === 'failed' || extractErr === 'pending') && (
                <div className="nm-bubble warn" style={{ maxWidth: '100%', marginBottom: 0 }}>Couldn’t read this file. Please try again or use a different file.</div>
              )}
              <div className="nm-btn-row" style={{ marginTop: 12 }}>
                <button className="nm-btn ghost" onClick={resetAll}>Cancel</button>
                <button className="nm-btn primary" disabled={extracting || committing || !extractRows || extractRows.length === 0} onClick={commit}>
                  {committing ? 'Saving…' : 'Approve & save'}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="nm-card" style={{ textAlign: 'center' }}>
              <h4 style={{ marginBottom: 6 }}>All done</h4>
              <div className="nm-kv" style={{ marginBottom: 12 }}>Your file has been saved.</div>
              <button className="nm-upload-btn secondary" onClick={resetAll}>Upload another file</button>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Composer / upload */}
        {(step === 'intro' || step === 'form') && (
          <div className="nm-composer">
            <input ref={fileInputRef} type="file" accept={ACCEPT} onChange={onFile} style={{ display: 'none' }} />
            <button className={`nm-upload-btn ${step === 'form' ? 'secondary' : ''}`} onClick={onPick}>
              <PaperclipIcon size={20} color={step === 'form' ? '#1A8AAF' : '#fff'} />
              {step === 'form' ? 'Replace file' : 'Upload a file'}
            </button>
            <div className="nm-accepted">PDF, XLSX, XLS, DOC, DOCX, PNG, JPG, JPEG • max 30 MB</div>
          </div>
        )}
      </div>
    </div>
  );
}
